import { Title } from "@/components/ui/Title.ts";
import { useEffect, useRef, useState } from "react";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs.ts";
import { IoChevronForward } from "react-icons/io5";
import { Table } from "@/components/ui/Table";
import { contactsApi } from "@/api/contacts.ts";
import {Link, useParams} from "react-router-dom";
import {
    DateBage,
    DateContainer,
    InputMessage,
    OutputMessage,
    MessagesContainer,
    MessageTime,
    SummaryContainer,
    SystemMessage
} from "@/pages/logs/LogInUserPage/styles.ts";
import { Divider } from "@/components/ui/Divider.ts";
import { fetchUser, setCurrentUser } from "@/store/logsSlice.ts";
import { fetchAssistant } from "@/store/assistantSlice.ts";
import { useAppDispatch } from "@/hooks/redux.ts";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { IMessageResponse } from "@/api/contacts.ts";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import AudioPlayer from "@/components/ui/AudioPlayer.tsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MarkdownContainer } from "@/components/ui/MarkdownContainer.tsx";

type Message = {
    date: string;
    content: string;
    time: string;
    role: string;
    id: number;
    isRead: boolean;
};

type GroupedMessages = {
    date: string;
    messages: Message[];
};

type MessagesState = {
    messages: GroupedMessages[];
    summary: string;
}[];

const TABLE_HEADERS = [
    { text: 'Сообщения', value: 'messages' },
    { text: 'Отчет', value: 'summary', width: '283px' }
];

function isValidUrl(str: string) {
    try {
        new URL(str);
        return true;
    } catch (e: any) {
        return false;
    }
}

export const LogInUserPage = () => {
    const [data, setTableData] = useState<MessagesState>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditUserName, setIsEditUserName] = useState<boolean>(false);
    const [username, setUsername] = useState<string>('');
    const { id, logId } = useParams<{ id: string; logId: string ; userId: string }>();
    const { user } = useSelector((state: RootState) => state.logs);
    const { assistant } = useSelector((state: RootState) => state.assistant)
    const dispatch = useAppDispatch();
    const summaryRef = useRef<HTMLDivElement>(null);
    const messagesRef = useRef<HTMLDivElement>(null);
    const msgRefs = useRef(new Map());
    const seenIdsRef = useRef(new Set());
    const pendingMessagesRef = useRef<Set<Message>>(new Set());
    const dateRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const processMessages = (messages: IMessageResponse[]) => {
        const grouped: Record<string, Message[]> = messages.reduce((acc, msg) => {
            const dateObj = new Date(msg.DateAdd);

            const formattedDate = dateObj.toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long'
            });

            const formattedTime = dateObj.toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });

            if (!acc[formattedDate]) {
                acc[formattedDate] = [];
            }
            if(msg.Role !== 'file' || msg.Role === 'file' && isValidUrl(msg.Content)) {
                acc[formattedDate].push({
                    date: formattedDate,
                    time: formattedTime,
                    content: msg.Content,
                    role: msg.Role,
                    isRead: msg.IsRead,
                    id: msg.ID
                });
            }

            return acc;
        }, {} as Record<string, Message[]>);

        const formattedMessages: GroupedMessages[] = Object.entries(grouped).map(([date, messages]) => ({
            date,
            messages
        }));
        setTableData([{ messages: formattedMessages, summary: '' }]);
    };

    const clearLog = () => {
        if(id && logId) {
            setIsLoading(true)
            contactsApi.clearLog(id, logId).then(() => {
                setTableData([])
            }).finally(() => {
                setIsLoading(false)
            })
        }
    }

    const onEditUserClick = () => {
        if(isEditUserName && logId) {
            dispatch(setCurrentUser({
                ...user,
                name: username
            }))
            setIsEditUserName(false)
            contactsApi.changeUserName(logId, username)
        } else {
            setUsername(user?.name);
            setIsEditUserName(true);
        }
    }

    const getData = async () => {
        if (!id || !logId) return;
        setIsLoading(true);

        try {
            const { data: messagesData } = await contactsApi.getSession(id, logId);
            processMessages(messagesData);
        } catch (error) {
            console.error('Ошибка при получении данных:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const scrollToDate = (date: string) => {
        const element = dateRefs.current[date];
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        if (summaryRef.current && messagesRef.current) {
            const summaryHeight = summaryRef.current.getBoundingClientRect().height;

            const availableHeight = window.innerHeight - 234;
            const calculatedHeight = summaryHeight > availableHeight ? `${summaryHeight}px` : `${availableHeight}px`;

            messagesRef.current.style.height = calculatedHeight;
        }
    }, [data[0]?.summary]);

    useEffect(() => {
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        const checkVisibility = () => {
            const container = messagesRef.current;
            if (!container) return;

            const containerRect = container.getBoundingClientRect();

            msgRefs.current.forEach((el, msg) => {
                if (el) {
                    const rect = el.getBoundingClientRect();
                    const isVisible =
                        rect.top >= containerRect.top && rect.top < containerRect.bottom;

                    if (isVisible && !seenIdsRef.current.has(msg.id)) {
                        pendingMessagesRef.current.add(msg);
                    }
                }
            });

            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                pendingMessagesRef.current.forEach((msg: Message) => {
                    if (!seenIdsRef.current.has(msg.id) && !msg.isRead) {
                        contactsApi.setAsRead({ id: msg.id });
                        seenIdsRef.current.add(msg.id);
                    }
                });

                pendingMessagesRef.current.clear();
            }, 300);
        };

        const container = messagesRef.current;
        if (container) {
            container.addEventListener("scroll", checkVisibility);
            checkVisibility();
        }

        return () => {
            if (container) container.removeEventListener("scroll", checkVisibility);
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [data]);

    useEffect(() => {
        if(logId && id) {
            dispatch(fetchUser(Number(logId)))
            dispatch(fetchAssistant(Number(id)))
        }
    }, [dispatch, id, logId]);

    return (
        <div>
            <div>
                <Title style={{ marginBottom: '24px' }}>
                    Логи {user?.name} в “{assistant?.Name}”
                </Title>
                <Breadcrumbs>
                    <Link to="/dashboard/logs">Логи</Link>
                    <IoChevronForward size={14} color="#000000"/>
                    <Link to={`/dashboard/logs/${assistant?.ID}/assistant`}>{assistant?.Name}</Link>
                    <IoChevronForward size={14} color="#000000"/>
                    <span>{user?.name}</span>
                </Breadcrumbs>
            </div>
            <Table
                headers={TABLE_HEADERS}
                data={data}
                tableKey="summary"
                loading={isLoading}
                withoutBorders
            >
                {({ column, item }) => {
                    if (column === 'messages') {
                        return (
                            <div ref={messagesRef} style={{ height: 'calc(100vh - 234px)', overflow: 'scroll', paddingLeft: '10px' }}>
                                {item.messages.length ? item.messages.map((group, index) => (
                                    <MessagesContainer key={index}>
                                        <DateContainer ref={(el) => (dateRefs.current[group.date] = el)}>
                                            <DateBage>{group.date}</DateBage>
                                        </DateContainer>
                                        {group.messages.map((msg) => {
                                            if(msg.role === 'user') {
                                                return (
                                                    <InputMessage
                                                        className='input-message'
                                                        key={msg.id}
                                                        ref={(el) => {
                                                            if (el) msgRefs.current.set(msg, el);
                                                            else msgRefs.current.delete(msg);
                                                        }}
                                                    >
                                                        <MarkdownContainer style={{ paddingRight: '28px' }}>
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                {msg.content}
                                                            </ReactMarkdown>
                                                        </MarkdownContainer>
                                                        <MessageTime>{msg.time}</MessageTime>
                                                    </InputMessage>
                                                )
                                            }
                                            if(msg.role === 'file') {
                                                return (
                                                    <InputMessage
                                                        className='input-message'
                                                        key={msg.id}
                                                        ref={(el) => {
                                                            if (el) msgRefs.current.set(msg, el);
                                                            else msgRefs.current.delete(msg);
                                                        }}
                                                    >
                                                        <AudioPlayer audioUrl={msg.content} />
                                                        <MessageTime>{msg.time}</MessageTime>
                                                    </InputMessage>
                                                )
                                            }
                                            if(msg.role === 'system') {
                                                return (
                                                    <SystemMessage
                                                        ref={(el) => {
                                                            if (el) msgRefs.current.set(msg, el);
                                                            else msgRefs.current.delete(msg);
                                                        }}
                                                        key={msg.id}
                                                    >
                                                        <MarkdownContainer>
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                {msg.content}
                                                            </ReactMarkdown>
                                                        </MarkdownContainer>
                                                    </SystemMessage>
                                                )
                                            }
                                            if (msg.role === 'assistant') {
                                                return (
                                                    <OutputMessage
                                                        ref={(el) => {
                                                            if (el) msgRefs.current.set(msg, el);
                                                            else msgRefs.current.delete(msg);
                                                        }}
                                                        className='output-message'
                                                        key={msg.id}
                                                    >
                                                        <MarkdownContainer style={{ paddingRight: '28px' }}>
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                {msg.content}
                                                            </ReactMarkdown>
                                                        </MarkdownContainer>
                                                        <MessageTime>{msg.time}</MessageTime>
                                                    </OutputMessage>
                                                )
                                            }
                                        })}
                                    </MessagesContainer>
                                )) : (
                                    <div>Нет сообщений</div>
                                )}
                            </div>
                        );
                    }
                    if(column === 'summary') {
                        return (
                            <SummaryContainer ref={summaryRef}>
                                <div style={{ padding: '20px 28px' }}>
                                    {isEditUserName ? (
                                        <Input value={username} onChange={(e) => setUsername(e.target.value)} />
                                    ) : (
                                        <div style={{ fontWeight: 600 }}>
                                            {user?.name}
                                        </div>
                                    )}
                                    <Button
                                        style={{ fontWeight: 400, marginTop: '8px' }}
                                        onClick={onEditUserClick} variant='link'
                                    >
                                        {isEditUserName ? 'Сохранить' : 'Изменить имя' }
                                    </Button>
                                    <Button
                                        style={{ marginTop: '8px' }}
                                        onClick={clearLog}
                                        variant="blue"
                                    >
                                        Очистить лог
                                    </Button>
                                </div>
                                <Divider />
                                <div style={{ padding: '20px 28px' }}>
                                    <div style={{fontWeight: 600, marginBottom: '18px'}}>Сообщения</div>
                                    <div>
                                        {item.messages.map((i, index) => (
                                            <div key={index}>
                                                <Button style={{ fontWeight: 400, marginRight: '8px' }} onClick={() => scrollToDate(i.date)} variant='link'>
                                                    {i.date}
                                                </Button>
                                                {i.messages.length}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </SummaryContainer>
                        )
                    }
                }}
            </Table>
        </div>
    );
};

